import { Component, Input, NgZone, OnInit, OnDestroy, EventEmitter, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { ChordService } from '../service/chord.service';
import { NgIf, NgClass } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatToolbar } from "@angular/material/toolbar";
import {ActivatedRoute, NavigationStart, Router, RouterLink} from "@angular/router";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Title } from "@angular/platform-browser";
import { MatDivider } from "@angular/material/divider";
import { MidiPlayerService } from "../service/midi-player.service";
import { HymnSelectionService } from "../service/hymn-selection.service";
import { ShareService } from "../service/share.service";
import { SidenavService } from "../service/sidenav.service";
import { SettingsService } from "../service/settings.service";
import { ModalService } from "../service/modal.service";
import { LayoutService } from "../service/layout.service";
import { ThemeService } from "../service/theme.service";
import { translation } from "../translation";
import { MusicControlComponent } from "../controls/music-control/music-control.component";
import { hymnsDetailed } from "../hymn/hymnsDetailed";
import { FontSizeComponent } from "../controls/font-size/font-size.component";
import {PinchZoomDirective} from "../directive/pinch-zoom.directive";
import {SwipeGestureDirective} from "../directive/swipe-gesture.directive";
import { ScrollContainerDirective } from "../directive/scroll-container.directive";

@Component({
  selector: 'app-chords-viewer',
  templateUrl: './chords-viewer.component.html',
  standalone: true,
  imports: [
    NgIf,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatToolbar,
    MusicControlComponent,
    MatMenuTrigger,
    RouterLink,
    MatDivider,
    PinchZoomDirective,
    // For bindings
    NgClass,
    // Gestures
    SwipeGestureDirective,
    ScrollContainerDirective,
  ],
  styleUrls: ['./chords-viewer.component.scss']
})
export class ChordsViewerComponent implements OnInit, OnDestroy {

  @Input() hymnNumber: number = 1;

  hymn: any = null;
  renderedHtml: string | null = null;
  isLoading = true;
  isAvailable = true;
  hasParseError = false;

  // Keep parsed song to allow re-rendering with transpose
  private songObj: any | null = null;

  // Transpose controls
  transposeSteps: number = 0; // -12..+12 typically

  // Font size controls (chords viewer has independent size setting)
  fontSizeMin = 8;
  fontSizeMax = 100;
  fontSize = this.settingService.getChordsFontSize();
  // Chords-specific typography
  chordsFontFamily: string | undefined = this.settingService.getChordsFontFamily();
  chordsFontWeight: number | undefined = this.settingService.getChordsFontWeight();

  // Theme mode for chords (reuse Sheet Music PDF theme setting)
  chordsThemeMode: 'white' | 'day' | 'night' | 'black' = this.settingService.getSheetMusicPdfThemeMode();

  protected readonly translation = translation;

  constructor(
    private http: HttpClient,
    private chordService: ChordService,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private bottomSheet: MatBottomSheet,
    private titleService: Title,
    public router: Router,
    public midiService: MidiPlayerService,
    public hymnSelectionService: HymnSelectionService,
    public shareService: ShareService,
    public sidenavService: SidenavService,
    protected settingService: SettingsService,
    protected modalService: ModalService,
    protected layoutService: LayoutService,
    private themeService: ThemeService
  ) {}

  // --- KEYBOARD SHORTCUTS ---
  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    this.ngZone.run(() => {
      if (!this.hymnSelectionService.searchFocus && !this.hymnSelectionService.dialpadActive) {
        switch (event.key) {
          case 'ArrowLeft':
          case 'b':
            if (this.hymn?.hymnNumber > 1) {
              this.router.navigate([`${translation.path_chords}/` + (this.hymn.hymnNumber - 1)]).then();
            }
            break;
          case 'ArrowRight':
          case 'n':
            if (this.hymn?.hymnNumber < hymnsDetailed[hymnsDetailed.length - 1].hymnNumber) {
              this.router.navigate([`${translation.path_chords}/` + (this.hymn.hymnNumber + 1)]).then();
            }
            break;
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpad();
            break;
          case 'l':
          case 'L':
            this.sidenavService.toggleList();
            break;
          case 'w':
          case 'W':
            this.hymnSelectionService.goToRandomHymn(false);
            break;
          case 'g':
          case 'G':
            this.hymnSelectionService.goToRandomHymn(true);
            break;
          case 'f':
          case 'F':
            this.layoutService.fullscreen ? this.layoutService.exitFullScreen() : this.layoutService.enterFullScreen();
            break;
          case 'v':
          case 'V':
            if (this.hymn) {
              this.router.navigate([`/${translation.path_present}/` + this.hymn.hymnNumber]);
            }
            break;
          case '+':
            if (this.fontSize < this.fontSizeMax) {
              this.updateFontSize(this.fontSize + 1);
            }
            break;
          case '-':
            if (this.fontSize > this.fontSizeMin) {
              this.updateFontSize(this.fontSize - 1);
            }
            break;
          case '<':
          case ',':
            this.transposeDown();
            break;
          case '>':
          case '.':
            this.transposeUp();
            break;
          case '0':
          case 'r':
          case 'R':
            this.resetTranspose();
            break;
          case 'i':
          case 'I':
            if(!this.modalService.active) {
              this.modalService.openDialog('shortcuts')
            } else {
              this.modalService.closeDialog();
            }
            break;
          case 'Escape':
            if(this.modalService.active) {
              this.modalService.closeDialog();
            }
            break;
        }
      } else {
        switch (event.key) {
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpadCancel();
            break;
          case 'Escape':
            this.hymnSelectionService.dialpadCancel();
            this.hymnSelectionService.searchFocus = false;
            break;
        }
      }
    });
  }

  // Re-render chord HTML from the stored Song object with transpose
  private renderChordHtml() {
    if (!this.songObj) {
      this.renderedHtml = null;
      return;
    }
    this.renderedHtml = this.chordService.renderSong(this.songObj, this.transposeSteps || 0);
  }

  transposeUp() {
    this.transposeSteps = (this.transposeSteps || 0) + 1;
    this.renderChordHtml();
  }
  transposeDown() {
    this.transposeSteps = (this.transposeSteps || 0) - 1;
    this.renderChordHtml();
  }
  resetTranspose() {
    this.transposeSteps = 0;
    this.renderChordHtml();
  }

  formatTranspose(n: number): string {
    if (n === 0) return '0';
    return n > 0 ? `+${n}` : `${n}`;
  }

  // --- Double-tap fullscreen helpers ---
  private lastTapTime = 0;
  private lastTapX = 0;
  private lastTapY = 0;
  private readonly doubleTapThresholdMs = 300;
  private readonly tapMoveTolerancePx = 30;

  private tapsClose(aX: number, aY: number, bX: number, bY: number): boolean {
    const dx = aX - bX;
    const dy = aY - bY;
    return (dx*dx + dy*dy) <= (this.tapMoveTolerancePx * this.tapMoveTolerancePx);
  }

  onContentDblClick(event: MouseEvent) {
    // Ignore if a font-size sheet or any modal flow is active
    if (this.layoutService.fontSizeSheetActive) return;
    if (this.layoutService.fullscreenSupported()) {
      this.layoutService.toggleFullScreen();
    }
  }

  onContentTouchEnd(event: TouchEvent) {
    // Ignore if a bottom sheet is open to prevent accidental toggles
    if (this.layoutService.fontSizeSheetActive) return;
    const touch = event.changedTouches && event.changedTouches[0];
    if (!touch) return;

    const now = Date.now();
    const x = touch.clientX;
    const y = touch.clientY;

    if (now - this.lastTapTime < this.doubleTapThresholdMs && this.tapsClose(x, y, this.lastTapX, this.lastTapY)) {
      // Double tap detected
      this.lastTapTime = 0; // reset
      if (this.layoutService.fullscreenSupported()) {
        event.preventDefault();
        event.stopPropagation();
        this.layoutService.toggleFullScreen();
      }
    } else {
      this.lastTapTime = now;
      this.lastTapX = x;
      this.lastTapY = y;
    }
  }

  ngOnInit() {

    this.settingService.setChordsViewMode();

    // Initialize transpose from playback (reuse if desired)
    try { this.transposeSteps = this.settingService.getPlaybackTranspose?.() ?? 0; } catch { this.transposeSteps = 0; }

    // keep font size in sync with chords-specific font size
    this.settingService.chordsFontSizeSubject.subscribe(() => {
      this.ngZone.run(() => {
        this.fontSize = this.settingService.getChordsFontSize();
      });
    });

    // Listen to chords-specific typography changes
    this.settingService.chordsFontFamilySubject.subscribe((fam) => {
      this.ngZone.run(() => {
        this.chordsFontFamily = fam ?? this.settingService.getFontFamily();
      });
    });

    this.settingService.chordsFontWeightSubject.subscribe((w) => {
      this.ngZone.run(() => {
        this.chordsFontWeight = (w ?? this.settingService.getFontWeight());
      });
    });

    // Dismiss bottom sheets on navigation
    this.router.events.subscribe(ev => {
      if (ev instanceof NavigationStart) {
        this.bottomSheet.dismiss();
      }
    });

    // Listen to route changes to update hymn content
    this.route.paramMap.subscribe(params => {
      const reference = params.get('reference');

      if (reference) {
        setTimeout(() => {
          if (this.layoutService.singleView) {
            this.sidenavService.closeList();
          }
        }, 10);

        this.getJSON(reference);
        this.hymnNumber = this.hymn.hymnNumber;
        this.hymnSelectionService.selectedHymn = this.hymn;
        this.settingService.addToHistory(this.hymn.hymnNumber);
      } else {
        this.hymn = this.settingService.getLastHymn();
        this.hymnNumber = this.hymn.hymnNumber;
        this.hymnSelectionService.selectedHymn = this.hymn;
      }

      // Metadata updates
      this.titleService.setTitle(`${translation.hymn} ${this.hymn.hymnNumber} - ${this.hymn.title} - ${translation.title_full}`);
      this.midiService.stop();

      // Core functionality: Load the actual chord file
      this.loadHymnChords();
    });
  }

  chordsThemeClass() {
    return {
      'chords-white': this.chordsThemeMode === 'white',
      'chords-day': this.chordsThemeMode === 'day',
      'chords-night': this.chordsThemeMode === 'night',
      'chords-black': this.chordsThemeMode === 'black',
    };
  }

  setChordsThemeMode(mode: 'white' | 'day' | 'night' | 'black') {
    this.chordsThemeMode = mode;
    // Reuse sheet music theme mode setting for consistency across viewers
    this.settingService.setSheetMusicPdfThemeMode(mode);
  }

  // --- SWIPE NAVIGATION ---
  lastHymnNumber = hymnsDetailed[hymnsDetailed.length - 1].hymnNumber;

  onSwipePrevious() {
    // Suppress when modals/sheets or search/dialpad are active
    if (this.layoutService.fontSizeSheetActive || this.modalService.active || this.hymnSelectionService.dialpadActive || this.hymnSelectionService.searchFocus) return;
    if (this.hymn && this.hymn.hymnNumber > 1) {
      this.ngZone.run(() => this.router.navigate([`${translation.path_chords}/` + (this.hymn.hymnNumber - 1)])).then();
    }
  }

  onSwipeNext() {
    if (this.layoutService.fontSizeSheetActive || this.modalService.active || this.hymnSelectionService.dialpadActive || this.hymnSelectionService.searchFocus) return;
    if (this.hymn && this.hymn.hymnNumber < this.lastHymnNumber) {
      this.ngZone.run(() => this.router.navigate([`${translation.path_chords}/` + (this.hymn.hymnNumber + 1)])).then();
    }
  }

  changeFontSize() {
    this.layoutService.fontSizeSheetActive = true;

    const bottomSheetRef = this.bottomSheet.open(FontSizeComponent, {
      data: {
        fontSizeEnabled: true,
        linesPerVerseEnabled: false,
        fontSizeMin: this.fontSizeMin,
        fontSizeMax: this.fontSizeMax,
        currentFontSize: this.fontSize,
        fontSizeChange: new EventEmitter<number>(),
        // chords-specific font family controls
        fonts: this.settingService.getAvailableFonts(),
        currentFontFamily: this.chordsFontFamily,
        fontFamilyChange: new EventEmitter<string | undefined>(),
        // chords-specific font weight controls
        fontWeightEnabled: true,
        currentFontWeight: this.chordsFontWeight,
        fontWeightChange: new EventEmitter<number | undefined>(),
        // not applicable in chords viewer
        textOutlineToggleEnabled: false,
        textAlignmentEnabled: false
      },
    });

    bottomSheetRef.instance.data.fontSizeChange?.subscribe((newFontSize: number) => {
      this.updateFontSize(newFontSize);
    });

    // font family change for chords viewer
    bottomSheetRef.instance.data.fontFamilyChange?.subscribe((fam: string | undefined) => {
      this.chordsFontFamily = fam;
      this.settingService.setChordsFontFamily(fam);
    });

    // font weight change for chords viewer
    bottomSheetRef.instance.data.fontWeightChange?.subscribe((w: number | undefined) => {
      this.chordsFontWeight = w;
      this.settingService.setChordsFontWeight(w);
    });

    bottomSheetRef.afterDismissed().subscribe(() => {
      this.layoutService.fontSizeSheetActive = false;
    });
  }

  // Updates local component state only (fast, no lag)
  previewFontSize(fontSize: number) {
    this.fontSize = fontSize;
  }

  // Updates chords-specific state / triggers storage save (run only at end)
  updateFontSize(fontSize: number) {
    this.settingService.setChordsFontSize(fontSize);
    this.previewFontSize(fontSize);
  }

  loadHymnChords() {
    this.isLoading = true;
    this.isAvailable = true;
    this.hasParseError = false; // <-- Reset the flag on load
    this.renderedHtml = null;
    this.songObj = null;

    // Standardize file name (e.g., 001.cho)
    const fileName = this.hymnNumber.toString().padStart(3, '0');
    const filePath = `assets/chords/${fileName}.cho`;

    this.http.get(filePath, { responseType: 'text' })
      .pipe(
        catchError((error) => {
          console.warn(`[Network Error] Chord file not found at: ${filePath}`, error);
          return of(null);
        })
      )
      .subscribe(data => {
        const isHtmlFallback = data?.trim().toLowerCase().startsWith('<!doctype') || data?.toLowerCase().includes('<html');

        if (data && data.trim().length > 0 && !isHtmlFallback) {
          const songObj = this.chordService.getSongObject(data);

          if (songObj) {
            // Success!
            this.songObj = songObj;
            this.renderChordHtml();
            this.isAvailable = true;
            this.hasParseError = false;
          } else {
            // File exists, but parsing failed
            console.error(`[Parse Error] Invalid ChordPro syntax in ${filePath}`);
            this.isAvailable = true;
            this.hasParseError = true; // <-- Set the flag to true
            this.renderedHtml = null;  // <-- Keep HTML null!
          }
        } else {
          // File doesn't exist or is empty
          console.warn(`[Content Error] ${filePath} is missing or returned index.html fallback.`);
          this.isAvailable = false;
          this.hasParseError = false;
          this.renderedHtml = null;
        }

        this.isLoading = false;
      });
  }

  public getJSON(reference: string) {
    this.hymn = hymnsDetailed[+reference - 1];
  }

  toggleSettingSidenav() {
    this.settingService.toggleSettings();
  }

  ngOnDestroy(): void {
    this.midiService.stop();
  }
}
